import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Eye, GripVertical, Trash2, Plus, Loader2,
  Type, Image as ImageIcon, MousePointerClick, LayoutGrid, Minus, Heading, Quote
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BLOCK_TYPES = [
  { type: 'hero', label: 'Hero', icon: Heading, desc: 'Bannière avec image ou vidéo (fond/contenu)' },
  { type: 'text', label: 'Texte', icon: Type, desc: 'Bloc de texte riche' },
  { type: 'image', label: 'Image', icon: ImageIcon, desc: 'Image pleine largeur' },
  { type: 'cta', label: 'Appel à l\'action', icon: MousePointerClick, desc: 'Bouton avec texte d\'accroche' },
  { type: 'grid', label: 'Grille', icon: LayoutGrid, desc: 'Grille de cartes (2-4 colonnes)' },
  { type: 'separator', label: 'Séparateur', icon: Minus, desc: 'Ligne de séparation' },
  { type: 'quote', label: 'Citation', icon: Quote, desc: 'Bloc de citation' },
];

const defaultContent = {
  hero: { title: 'Titre', subtitle: 'Sous-titre', bg_color: '#1E3A5F', text_color: '#FFFFFF', media_type: 'none', media_url: '', media_position: 'background', overlay_opacity: 50 },
  text: { body: 'Votre texte ici...', alignment: 'left' },
  image: { url: '', alt: '', caption: '' },
  cta: { title: 'Prêt à participer ?', description: 'Rejoignez-nous', button_text: 'En savoir plus', button_url: '/participer', bg_color: '#1E3A5F' },
  grid: { columns: 3, items: [{ title: 'Carte 1', description: 'Description', icon: '' }, { title: 'Carte 2', description: 'Description', icon: '' }, { title: 'Carte 3', description: 'Description', icon: '' }] },
  separator: { style: 'line' },
  quote: { text: 'Une citation inspirante...', author: 'Auteur', role: '' },
};

function SortableBlock({ block, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' };
  const meta = BLOCK_TYPES.find(b => b.type === block.type);
  const Icon = meta?.icon || Type;

  return (
    <div ref={setNodeRef} style={style} className="group" data-testid={`block-${block.id}`}>
      <Card className="border border-[#E1E8F0] shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3 p-3 bg-[#F7F9FC] border-b border-[#E1E8F0] rounded-t-lg">
          <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-[#94A3B8] hover:text-[#6B7C93]" data-testid={`drag-${block.id}`}>
            <GripVertical className="w-4 h-4" />
          </button>
          <Icon className="w-4 h-4 text-[#F5A623]" />
          <span className="text-sm font-medium text-[#1E3A5F] flex-1">{meta?.label || block.type}</span>
          <Button variant="ghost" size="sm" onClick={() => onEdit(block)} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-block-${block.id}`}>
            <Type className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(block.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500" data-testid={`delete-block-${block.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
        <CardContent className="p-4">
          <BlockPreview block={block} />
        </CardContent>
      </Card>
    </div>
  );
}

function BlockPreview({ block }) {
  const c = block.content || {};
  switch (block.type) {
    case 'hero':
      return (
        <div className="rounded-lg overflow-hidden relative" style={{ backgroundColor: c.bg_color || '#1E3A5F' }}>
          {/* Background media */}
          {c.media_position === 'background' && c.media_url && (
            <div className="absolute inset-0">
              {c.media_type === 'video' ? (
                <video src={c.media_url} muted loop className="w-full h-full object-cover" />
              ) : (
                <img src={c.media_url} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-black" style={{ opacity: (c.overlay_opacity || 50) / 100 }} />
            </div>
          )}
          {/* Content */}
          <div className={`relative z-10 p-6 ${c.media_position === 'content' && c.media_url ? 'flex items-center gap-6' : 'text-center'}`}>
            <div className={c.media_position === 'content' && c.media_url ? 'flex-1' : ''}>
              <h2 className="text-xl font-bold" style={{ color: c.text_color || '#FFF' }}>{c.title || 'Titre'}</h2>
              <p className="text-sm mt-1 opacity-70" style={{ color: c.text_color || '#FFF' }}>{c.subtitle || ''}</p>
            </div>
            {/* Content media (side) */}
            {c.media_position === 'content' && c.media_url && (
              <div className="w-1/3 flex-shrink-0 rounded-lg overflow-hidden">
                {c.media_type === 'video' ? (
                  <video src={c.media_url} muted controls className="w-full h-24 object-cover rounded-lg" />
                ) : (
                  <img src={c.media_url} alt="" className="w-full h-24 object-cover rounded-lg" />
                )}
              </div>
            )}
          </div>
          {/* Type badge */}
          {c.media_type !== 'none' && c.media_url && (
            <div className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded text-[10px] font-semibold bg-black/40 text-white">
              {c.media_type === 'video' ? 'Vidéo' : 'Image'} · {c.media_position === 'background' ? 'Fond' : 'Contenu'}
            </div>
          )}
        </div>
      );
    case 'text':
      return <p className="text-sm text-[#4A5568] whitespace-pre-line" style={{ textAlign: c.alignment || 'left' }}>{c.body || 'Texte...'}</p>;
    case 'image':
      return c.url ? (
        <div><img src={c.url} alt={c.alt} className="w-full max-h-40 object-cover rounded-lg" />{c.caption && <p className="text-xs text-[#94A3B8] mt-1 text-center">{c.caption}</p>}</div>
      ) : <div className="h-24 bg-[#F0F2F5] rounded-lg flex items-center justify-center"><ImageIcon className="w-8 h-8 text-[#D1D5DB]" /></div>;
    case 'cta':
      return (
        <div className="rounded-lg p-4 text-center" style={{ backgroundColor: c.bg_color || '#1E3A5F' }}>
          <p className="text-white font-semibold">{c.title}</p>
          <p className="text-white/70 text-sm">{c.description}</p>
          <span className="inline-block mt-2 px-4 py-1.5 bg-[#F5A623] text-[#1E3A5F] text-sm font-semibold rounded-lg">{c.button_text}</span>
        </div>
      );
    case 'grid':
      return (
        <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${c.columns || 3}, 1fr)` }}>
          {(c.items || []).map((item, i) => (
            <div key={`grid-${item.title}-${i}`} className="bg-[#F7F9FC] rounded-lg p-3 text-center">
              <p className="text-sm font-semibold text-[#1E3A5F]">{item.title}</p>
              <p className="text-xs text-[#6B7C93] mt-0.5">{item.description}</p>
            </div>
          ))}
        </div>
      );
    case 'separator':
      return <hr className="border-[#E1E8F0]" />;
    case 'quote':
      return (
        <blockquote className="border-l-4 border-[#F5A623] pl-4 italic">
          <p className="text-sm text-[#4A5568]">"{c.text}"</p>
          {c.author && <p className="text-xs text-[#94A3B8] mt-1">— {c.author}{c.role ? `, ${c.role}` : ''}</p>}
        </blockquote>
      );
    default:
      return <p className="text-sm text-[#94A3B8]">Bloc inconnu</p>;
  }
}

function BlockEditor({ block, onSave, onClose, token }) {
  const [content, setContent] = useState(block?.content || {});
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaTarget, setMediaTarget] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => { setContent(block?.content || {}); }, [block?.content]);

  const openMedia = (target) => {
    setMediaTarget(target);
    setMediaOpen(true);
    axios.get(`${API}/gallery/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setGalleryImages(res.data))
      .catch(() => {});
  };

  const selectMedia = (url) => {
    if (mediaTarget === 'image_url') setContent({ ...content, image_url: url });
    else if (mediaTarget === 'url') setContent({ ...content, url });
    else if (mediaTarget === 'media_url') setContent({ ...content, media_url: url });
    setMediaOpen(false);
  };

  const updateGridItem = (index, field, value) => {
    const items = [...(content.items || [])];
    items[index] = { ...items[index], [field]: value };
    setContent({ ...content, items });
  };

  const addGridItem = () => {
    const items = [...(content.items || []), { title: 'Nouveau', description: 'Description', icon: '' }];
    setContent({ ...content, items });
  };

  const removeGridItem = (index) => {
    const items = (content.items || []).filter((_, i) => i !== index);
    setContent({ ...content, items });
  };

  const renderFields = () => {
    switch (block?.type) {
      case 'hero': return (
        <div className="space-y-3">
          <div><Label>Titre</Label><Input value={content.title || ''} onChange={(e) => setContent({ ...content, title: e.target.value })} /></div>
          <div><Label>Sous-titre</Label><Input value={content.subtitle || ''} onChange={(e) => setContent({ ...content, subtitle: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Couleur fond</Label><Input type="color" value={content.bg_color || '#1E3A5F'} onChange={(e) => setContent({ ...content, bg_color: e.target.value })} /></div>
            <div><Label>Couleur texte</Label><Input type="color" value={content.text_color || '#FFFFFF'} onChange={(e) => setContent({ ...content, text_color: e.target.value })} /></div>
          </div>

          {/* Media section */}
          <div className="border-t border-[#E1E8F0] pt-3 mt-3">
            <Label className="text-sm font-semibold">Média</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs text-[#6B7C93]">Type de média</Label>
                <Select value={content.media_type || 'none'} onValueChange={(v) => setContent({ ...content, media_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Vidéo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B7C93]">Position</Label>
                <Select value={content.media_position || 'background'} onValueChange={(v) => setContent({ ...content, media_position: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="background">Arrière-plan</SelectItem>
                    <SelectItem value="content">Contenu principal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {content.media_type && content.media_type !== 'none' && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs text-[#6B7C93]">{content.media_type === 'video' ? 'URL de la vidéo' : 'URL de l\'image'}</Label>
                    <Input value={content.media_url || ''} onChange={(e) => setContent({ ...content, media_url: e.target.value })} placeholder={content.media_type === 'video' ? 'https://...video.mp4' : 'https://...image.jpg'} />
                  </div>
                  {content.media_type === 'image' && (
                    <Button variant="outline" size="sm" onClick={() => openMedia('media_url')}><ImageIcon className="w-4 h-4" /></Button>
                  )}
                </div>

                {/* Preview */}
                {content.media_url && (
                  <div className="rounded-lg overflow-hidden border border-[#E1E8F0]">
                    {content.media_type === 'video' ? (
                      <video src={content.media_url} controls muted className="w-full h-28 object-cover" />
                    ) : (
                      <img src={content.media_url} alt="Aperçu" className="w-full h-28 object-cover" />
                    )}
                  </div>
                )}

                {content.media_position === 'background' && (
                  <div>
                    <Label className="text-xs text-[#6B7C93]">Opacité de l'overlay ({content.overlay_opacity || 50}%)</Label>
                    <input type="range" min="0" max="90" value={content.overlay_opacity || 50} onChange={(e) => setContent({ ...content, overlay_opacity: parseInt(e.target.value) })} className="w-full mt-1 accent-[#1E3A5F]" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
      case 'text': return (
        <div className="space-y-3">
          <div><Label>Contenu</Label><Textarea value={content.body || ''} onChange={(e) => setContent({ ...content, body: e.target.value })} rows={8} /></div>
          <div><Label>Alignement</Label>
            <Select value={content.alignment || 'left'} onValueChange={(v) => setContent({ ...content, alignment: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centre</SelectItem><SelectItem value="right">Droite</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
      case 'image': return (
        <div className="space-y-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1"><Label>URL Image</Label><Input value={content.url || ''} onChange={(e) => setContent({ ...content, url: e.target.value })} placeholder="https://..." /></div>
            <Button variant="outline" size="sm" onClick={() => openMedia('url')}><ImageIcon className="w-4 h-4" /></Button>
          </div>
          {content.url && <img src={content.url} alt="" className="w-full max-h-40 object-cover rounded-lg" />}
          <div><Label>Texte alternatif</Label><Input value={content.alt || ''} onChange={(e) => setContent({ ...content, alt: e.target.value })} /></div>
          <div><Label>Légende</Label><Input value={content.caption || ''} onChange={(e) => setContent({ ...content, caption: e.target.value })} /></div>
        </div>
      );
      case 'cta': return (
        <div className="space-y-3">
          <div><Label>Titre</Label><Input value={content.title || ''} onChange={(e) => setContent({ ...content, title: e.target.value })} /></div>
          <div><Label>Description</Label><Input value={content.description || ''} onChange={(e) => setContent({ ...content, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Texte bouton</Label><Input value={content.button_text || ''} onChange={(e) => setContent({ ...content, button_text: e.target.value })} /></div>
            <div><Label>URL bouton</Label><Input value={content.button_url || ''} onChange={(e) => setContent({ ...content, button_url: e.target.value })} /></div>
          </div>
          <div><Label>Couleur fond</Label><Input type="color" value={content.bg_color || '#1E3A5F'} onChange={(e) => setContent({ ...content, bg_color: e.target.value })} /></div>
        </div>
      );
      case 'grid': return (
        <div className="space-y-3">
          <div><Label>Colonnes</Label>
            <Select value={String(content.columns || 3)} onValueChange={(v) => setContent({ ...content, columns: parseInt(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="2">2</SelectItem><SelectItem value="3">3</SelectItem><SelectItem value="4">4</SelectItem></SelectContent>
            </Select>
          </div>
          <Label>Cartes</Label>
          {(content.items || []).map((item, i) => (
            <div key={`edit-grid-${i}`} className="flex gap-2 items-start p-2 bg-[#F7F9FC] rounded-lg">
              <div className="flex-1 space-y-1">
                <Input value={item.title} onChange={(e) => updateGridItem(i, 'title', e.target.value)} placeholder="Titre" className="text-sm" />
                <Input value={item.description} onChange={(e) => updateGridItem(i, 'description', e.target.value)} placeholder="Description" className="text-sm" />
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeGridItem(i)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addGridItem}><Plus className="w-3.5 h-3.5 mr-1" />Ajouter une carte</Button>
        </div>
      );
      case 'quote': return (
        <div className="space-y-3">
          <div><Label>Citation</Label><Textarea value={content.text || ''} onChange={(e) => setContent({ ...content, text: e.target.value })} rows={3} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Auteur</Label><Input value={content.author || ''} onChange={(e) => setContent({ ...content, author: e.target.value })} /></div>
            <div><Label>Fonction</Label><Input value={content.role || ''} onChange={(e) => setContent({ ...content, role: e.target.value })} /></div>
          </div>
        </div>
      );
      case 'separator': return <p className="text-sm text-[#94A3B8]">Aucune configuration nécessaire pour le séparateur.</p>;
      default: return null;
    }
  };

  return (
    <>
      <Dialog open={!!block} onOpenChange={() => onClose()}>
        <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier le bloc : {BLOCK_TYPES.find(b => b.type === block?.type)?.label}</DialogTitle></DialogHeader>
          <div className="mt-2">{renderFields()}</div>
          <Button onClick={() => onSave(content)} className="w-full mt-4 bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="save-block-btn">Appliquer</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={mediaOpen} onOpenChange={() => setMediaOpen(false)}>
        <DialogContent className="bg-white max-w-3xl max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sélectionner une image</DialogTitle></DialogHeader>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {galleryImages.map(img => (
              <button key={img.id} onClick={() => selectMedia(`${BACKEND_URL}${img.url}`)} className="rounded-lg overflow-hidden border-2 border-transparent hover:border-[#F5A623]">
                <img src={`${BACKEND_URL}${img.url}`} alt={img.title} className="w-full h-20 object-cover" />
              </button>
            ))}
            {galleryImages.length === 0 && <p className="col-span-4 text-center py-8 text-[#94A3B8]">Aucune image dans la bibliothèque</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function PageEditor() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchPage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pages/${pageId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPage(res.data);
      setBlocks(res.data.blocks || []);
    } catch (e) { toast.error('Page non trouvée'); navigate('/admin/site-builder'); }
    finally { setLoading(false); }
  }, [pageId, token, navigate]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const savePage = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/pages/${pageId}`, { blocks }, { headers });
      toast.success('Page sauvegardée');
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      type,
      content: { ...(defaultContent[type] || {}) },
      order: blocks.length,
    };
    setBlocks([...blocks, newBlock]);
    setAddBlockOpen(false);
    toast.success('Bloc ajouté');
  };

  const deleteBlock = (blockId) => {
    setBlocks(blocks.filter(b => b.id !== blockId));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex(b => b.id === active.id);
    const newIndex = blocks.findIndex(b => b.id === over.id);
    setBlocks(arrayMove(blocks, oldIndex, newIndex));
  };

  const saveBlockContent = (content) => {
    setBlocks(blocks.map(b => b.id === editingBlock.id ? { ...b, content } : b));
    setEditingBlock(null);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#94A3B8]" /></div>;

  return (
    <div data-testid="page-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin/site-builder')} className="text-[#6B7C93]" data-testid="back-to-pages">
            <ArrowLeft className="w-4 h-4 mr-2" />Retour
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[#1E3A5F]">{page?.title}</h1>
            <p className="text-xs text-[#94A3B8] font-mono">/{page?.slug}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAddBlockOpen(true)} data-testid="add-block-btn">
            <Plus className="w-4 h-4 mr-2" />Ajouter un bloc
          </Button>
          <Button onClick={savePage} disabled={saving} className="bg-[#F5A623] hover:bg-[#E09615] text-[#1E3A5F] font-semibold" data-testid="save-page-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Blocks list */}
      <div className="max-w-3xl mx-auto space-y-3">
        {blocks.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-[#E1E8F0] rounded-xl">
            <LayoutGrid className="w-12 h-12 text-[#D1D5DB] mx-auto mb-3" />
            <p className="text-[#6B7C93] font-medium">Page vide</p>
            <p className="text-sm text-[#94A3B8] mt-1">Ajoutez des blocs pour construire votre page</p>
            <Button variant="outline" className="mt-4" onClick={() => setAddBlockOpen(true)} data-testid="add-first-block">
              <Plus className="w-4 h-4 mr-2" />Ajouter un bloc
            </Button>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
              {blocks.map((block) => (
                <SortableBlock
                  key={block.id}
                  block={block}
                  onEdit={setEditingBlock}
                  onDelete={deleteBlock}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Add Block Dialog */}
      <Dialog open={addBlockOpen} onOpenChange={setAddBlockOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Ajouter un bloc</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {BLOCK_TYPES.map((bt) => (
              <button
                key={bt.type}
                onClick={() => addBlock(bt.type)}
                className="flex items-start gap-3 p-3 rounded-lg border border-[#E1E8F0] hover:border-[#F5A623] hover:bg-[#FFFBF0] transition-colors text-left"
                data-testid={`add-block-${bt.type}`}
              >
                <bt.icon className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#1E3A5F]">{bt.label}</p>
                  <p className="text-xs text-[#94A3B8]">{bt.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Block Editor */}
      {editingBlock && (
        <BlockEditor
          block={editingBlock}
          onSave={saveBlockContent}
          onClose={() => setEditingBlock(null)}
          token={token}
        />
      )}
    </div>
  );
}
